<template>
    <section>
        <CompaniesFilters />
        <BmvTable
            :columns="tableColumns"
            :data="companies && companies.data ? companies.data : []"
            :pagination="true"
            :total-results="companies ? companies.total : 0"
            :total-pages="companies ? Math.ceil(companies.total / companies.per_page) : 0"
            :current-page="companies ? companies.current_page : 1"
            :per-page="companies ? companies.per_page : 20"
            :sort-field="config.sort"
            :sort-direction="config.sortDirection"
            @sort="onSort"
            @page-change="onPageChange"
        >
            <template #cell-id="{ row }">
                <span class="tc-id">#{{ row.id }}</span>
            </template>

            <template #cell-name="{ row }">
                <router-link :to="`/admin/companies/${row.id}`" class="tc-name">
                    {{ row.name }}
                </router-link>
            </template>

            <template #cell-active="{ row }">
                <span
                    class="tc-status-pill"
                    :class="row.active ? 'tc-status-pill--ok' : 'tc-status-pill--bad'"
                >
                    <span class="tc-status-dot"></span>
                    {{ row.active ? $t('companies.user.active.actived') : $t('companies.user.active.nonactive') }}
                </span>
            </template>

            <template #cell-actions="{ row }">
                <div class="tc-actions">
                    <router-link
                        :to="`/admin/companies/${row.id}`"
                        class="tc-btn tc-btn--ghost"
                        :title="$t('form_types.view')"
                    >
                        <i class="fal fa-eye"></i>
                    </router-link>
                    <router-link
                        :to="`/admin/companies/${row.id}/edit`"
                        class="tc-btn tc-btn--ghost"
                        :title="$t('common.edit')"
                    >
                        <i class="fal fa-pen"></i>
                    </router-link>
                </div>
            </template>
        </BmvTable>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'
import BmvTable from '@/components/UI/BmvTable'
import CompaniesFilters from '@/components/Model/Admin/CompaniesFilters'

export default {
    name: 'TabCompanies',
    components: { BmvTable, CompaniesFilters },
    data() {
        return {
            tableColumns: [
                { field: 'id', label: this.$t('admin.companies.col_id'), sortable: true, width: '70px' },
                { field: 'name', label: this.$t('admin.companies.col_name'), sortable: true },
                { field: 'slug', label: 'Slug', sortable: true, width: '160px' },
                { field: 'active', label: this.$t('admin.companies.col_status'), sortable: true, width: '150px' },
                { field: 'actions', label: this.$t('admin.companies.col_actions'), width: '110px', align: 'center' }
            ]
        }
    },
    computed: {
        ...mapState('admin', {
            companies: (state) => state.companies,
            config: (state) => state.companiesConfig,
            statuses: (state) => state.statuses
        })
    },
    methods: {
        onSort({ field, direction }) {
            this.config.page = 1
            this.config.sort = field
            this.config.sortDirection = direction
            this.$store.dispatch('admin/getCompanies')
        },
        onPageChange(page) {
            this.config.page = page
            this.$store.dispatch('admin/getCompanies')
        }
    }
}
</script>

<style scoped>
.tc-id {
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-4);
    font-variant-numeric: tabular-nums;
}

.tc-name {
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.tc-name:hover {
    color: var(--v2-primary);
}

.tc-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
}

.tc-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}

.tc-status-pill--ok {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.tc-status-pill--ok .tc-status-dot {
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.tc-status-pill--bad {
    background: rgba(148, 163, 184, 0.18);
    color: var(--v2-text-3);
}

.tc-status-pill--bad .tc-status-dot {
    background: var(--v2-text-4);
}

.tc-actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.tc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
}

.tc-btn i {
    font-size: 0.75rem;
}

.tc-btn--ghost:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}
</style>
